import { Box, Select, SelectProps } from "@mui/material";
import { KeyboardArrowDown as KeyboardArrowDownIcon } from "@mui/icons-material";
import * as React from "react";
import { useCallback, useRef, useState } from "react";
import { styled } from "@mui/system";
import { SelectInputProps } from "@mui/material/Select/SelectInput";
import { makeStyles } from "tss-react/mui";
import { colors } from "~/theme";
import { DropdownChip } from "./DropdownChip";

const StyledSelect = styled(Select)({
    color: colors.black,
    borderRadius: "8px",

    '&:not([aria-expanded="true"])': {
        backgroundColor: colors.black03,

        "& ~ fieldset": {
            border: "none",
        },
    },

    '&[aria-expanded="true"]': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,

        "& ~ fieldset": {
            borderColor: colors.black80,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        },
    },
});

const useStyles = makeStyles<{
    transition?: string;
    width?: number;
    height?: number;
}>()((theme, { transition, width, height }) => ({
    paper: {
        border: `1px solid ${colors.black80}`,
        boxShadow: "none",
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderTopWidth: 0,
        transition,
        width,
        maxHeight: height,
    },
    list: {
        "& .MuiListItem-root.Mui-selected, .MuiListItem-root.Mui-selected:hover":
            {
                backgroundColor: colors.black03,
            },
    },
}));

const renderMultiple = (onChange: SelectInputProps["onChange"]) =>
    function (value: string[]) {
        return (
            <Box display="flex" flexWrap="wrap">
                {/* eslint-disable-next-line react/destructuring-assignment */}
                {value.map((renderEl) => (
                    <DropdownChip
                        key={renderEl}
                        selected={renderEl}
                        onDelete={(e: React.SyntheticEvent) => {
                            (
                                e as unknown as Event & {
                                    target: { value: string[]; name: string };
                                }
                            ).target.value = [...value].filter(
                                (el) => el !== renderEl,
                            );
                            onChange?.(
                                e as unknown as Event & {
                                    target: { value: string[]; name: string };
                                },
                                null,
                            );
                        }}
                    />
                ))}
            </Box>
        );
    };

export function Dropdown({
    width,
    placeholder,
    transition,
    ...props
}: SelectProps & { width?: number; transition?: string }) {
    const [menuWidth, setMenuWidth] = useState<number | undefined>(width);
    const [menuHeight, setMenuHeight] = useState<number | undefined>(undefined);
    const ref = useRef<HTMLDivElement>(null);

    const { classes } = useStyles({
        transition,
        width: menuWidth,
        height: menuHeight,
    });

    const getRenderValue = () => {
        if (props.value) {
            if (props.multiple) {
                return renderMultiple(props.onChange);
            }
            return undefined;
        }
        return function () {
            return (
                <Box component="span" color={colors.black40}>
                    {placeholder}
                </Box>
            );
        };
    };

    const onOpen = useCallback(() => {
        if (ref?.current?.scrollWidth) {
            const element = ref?.current;
            setMenuWidth(element.scrollWidth);

            const domRect = element.getBoundingClientRect();
            const spaceBelow = window.innerHeight - domRect.bottom;

            const TRANSFORM_ORIGIN = 16;
            setMenuHeight(spaceBelow - TRANSFORM_ORIGIN);
        }
    }, [ref]);

    return (
        <Box width={width}>
            <StyledSelect
                sx={{
                    width,
                }}
                onOpen={onOpen}
                ref={ref}
                MenuProps={{
                    anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                    },
                    transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                    },
                    classes,
                }}
                displayEmpty
                renderValue={getRenderValue()}
                IconComponent={KeyboardArrowDownIcon}
                variant="outlined"
                {...props}
            />
        </Box>
    );
}

export { MenuItem } from "@mui/material";
